import React, { useState, useEffect } from 'react';
import APIService from '../../services/APIService';
import { getSolvedSet } from './dsaProgress';
import {
  IconCode, IconSearch, IconChevDown, IconChevRight, IconCheck,
  IconArrowLeft, IconArrowRight,
} from './icons';

const DIFFICULTIES = ['Easy', 'Medium', 'Hard'];
const PAGE_SIZE = 25;
const diffClass = (d) => `nano-dsa-tag diff-${(d || '').toLowerCase()}`;

/*
 * DsaNav — DSA browse page. Server-side paginated problem list (25/page) from
 * the SQLite-backed /dsa/questions, plus topic cards from /dsa/topics. Picking
 * a problem calls onOpen(question_id).
 */
const DsaNav = ({ onOpen }) => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const [page, setPage] = useState(0);          // 0-based
  const [difficulty, setDifficulty] = useState('');
  const [topic, setTopic] = useState('');
  const [search, setSearch] = useState('');
  const [showAllTopics, setShowAllTopics] = useState(false);

  const solved = getSolvedSet();
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Topics once.
  useEffect(() => {
    APIService.getDsaTopics().then(setTopics).catch(() => {});
  }, []);

  // Reset to page 0 whenever a filter changes.
  useEffect(() => { setPage(0); }, [difficulty, topic, search]);

  // Fetch the current page (debounced so typing in search doesn't spam).
  useEffect(() => {
    let active = true;
    setLoading(true);
    const t = setTimeout(() => {
      APIService.listDsaQuestions({ difficulty, tag: topic, search, offset: page * PAGE_SIZE, limit: PAGE_SIZE })
        .then((res) => { if (active) { setItems(res.items || []); setTotal(res.total || 0); setErr(''); } })
        .catch((e) => { if (active) setErr(e.message || 'Could not load problems.'); })
        .finally(() => { if (active) setLoading(false); });
    }, 220);
    return () => { active = false; clearTimeout(t); };
  }, [page, difficulty, topic, search]);

  const topicList = showAllTopics ? topics : topics.slice(0, 12);
  const go = (p) => setPage(Math.min(Math.max(0, p), pageCount - 1));

  return (
    <div className="nano-dsanav">
      <div className="nano-dsanav-head">
        <div className="nano-dsanav-title"><IconCode size={20} /> DSA Problems</div>
        <div className="nano-dsanav-sub">{solved.size} solved · {total.toLocaleString()} problems</div>
      </div>

      {err && <div className="nano-dsanav-err">{err}</div>}

      <div className="nano-dsanav-grid">
        {/* LEFT — problem list */}
        <section className="nano-dsanav-card">
          <div className="nano-dsanav-card-h">All Problems</div>

          <div className="nano-dsanav-filters">
            <div className="nano-dsanav-select">
              <select value={difficulty} onChange={(e) => setDifficulty(e.target.value)}>
                <option value="">Difficulty</option>
                {DIFFICULTIES.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
              <IconChevDown size={14} />
            </div>
            <div className="nano-dsanav-select">
              <select value={topic} onChange={(e) => setTopic(e.target.value)}>
                <option value="">Topics</option>
                {topics.map((t) => <option key={t.topic} value={t.topic}>{t.topic} ({t.count})</option>)}
              </select>
              <IconChevDown size={14} />
            </div>
            <div className="nano-dsanav-search">
              <IconSearch size={15} />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search problems…" />
            </div>
          </div>

          <div className="nano-dsanav-table">
            <div className="nano-dsanav-tr head">
              <span className="c-num">#</span>
              <span className="c-title">Title</span>
              <span className="c-diff">Difficulty</span>
              <span className="c-acc">Acceptance</span>
              <span className="c-freq">Frequency</span>
            </div>
            {loading ? (
              <div className="nano-dsanav-empty">Loading problems…</div>
            ) : items.length === 0 ? (
              <div className="nano-dsanav-empty">No problems match your filters.</div>
            ) : items.map((q) => (
              <button key={q.question_id} className="nano-dsanav-tr row" onClick={() => onOpen(q.question_id)} type="button">
                <span className="c-num">
                  {solved.has(q.question_id) ? <span className="nano-dsanav-solved"><IconCheck size={12} /></span> : q.question_id}
                </span>
                <span className="c-title">{q.title}</span>
                <span className="c-diff"><span className={diffClass(q.difficulty)}>{q.difficulty}</span></span>
                <span className="c-acc">{q.acceptance != null ? `${Math.round(q.acceptance * 100)}%` : '—'}</span>
                <span className="c-freq">
                  <span className="nano-dsanav-bar"><span style={{ width: `${Math.round((q.frequency || 0) * 100)}%` }} /></span>
                </span>
              </button>
            ))}
          </div>

          {/* Pagination */}
          {total > PAGE_SIZE && (
            <div className="nano-dsanav-pager">
              <button onClick={() => go(0)} disabled={page === 0} type="button" title="First">« First</button>
              <button onClick={() => go(page - 1)} disabled={page === 0} type="button"><IconArrowLeft size={13} /> Prev</button>
              <span className="nano-dsanav-page">Page {page + 1} of {pageCount}</span>
              <button onClick={() => go(page + 1)} disabled={page >= pageCount - 1} type="button">Next <IconArrowRight size={13} /></button>
              <button onClick={() => go(pageCount - 1)} disabled={page >= pageCount - 1} type="button" title="Last">Last »</button>
            </div>
          )}
        </section>

        {/* RIGHT — topic cards (name + count only) */}
        <section className="nano-dsanav-card">
          <div className="nano-dsanav-card-h">Topics / Patterns</div>
          <div className="nano-dsanav-topics">
            {topicList.map((t) => (
              <button
                key={t.topic}
                className={`nano-dsanav-topic${topic === t.topic ? ' active' : ''}`}
                onClick={() => setTopic(topic === t.topic ? '' : t.topic)}
                type="button"
              >
                <span className="nano-dsanav-topic-name">{t.topic}</span>
                <span className="nano-dsanav-topic-count">{t.count}</span>
              </button>
            ))}
          </div>
          {topics.length > 12 && (
            <button className="nano-dsanav-viewall" onClick={() => setShowAllTopics((v) => !v)} type="button">
              {showAllTopics ? 'Show less' : 'View All Topics'} <IconChevRight size={14} />
            </button>
          )}
        </section>
      </div>
    </div>
  );
};

export default DsaNav;
